import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

export default function AccessoriArticle() {
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Itaobuy Spreadsheet Accessori: Occhiali, Orologi e Gioielli con Foto QC',
    description: 'Scopri i migliori accessori su Itaobuy Spreadsheet: occhiali da sole, orologi, gioielli e articoli di tendenza. Guida italiana completa con foto QC dettagliate.',
    author: { '@type': 'Organization', name: 'Itaobuy Spread' },
    datePublished: '2026-07-14',
    inLanguage: 'it',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: 'Quali accessori posso trovare su Itaobuy Spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'Occhiali da sole, orologi di tendenza, gioielli come collane e bracciali, portafogli, cinture e piccoli articoli di pelletteria. Ogni accessorio ha foto QC.' } },
      { '@type': 'Question', name: 'Le foto QC degli accessori mostrano i dettagli più piccoli?', acceptedAnswer: { '@type': 'Answer', text: 'Sì, le foto QC per gli accessori sono particolarmente dettagliate: mostrano chiusure, incisioni, materiali e finiture che fanno la differenza nella qualità.' } },
      { '@type': 'Question', name: 'Come trovo rapidamente gli accessori su Itaobuy Spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'Apri lo spreadsheet e seleziona la categoria Accessori. Puoi filtrare per tipo di articolo, prezzo e popolarità per trovare subito ciò che cerchi.' } },
    ],
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://itaobuyspread.mom';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: 'Itaobuy Spreadsheet Accessori', item: `${siteUrl}/itaobuy-spreadsheet-accessori` },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />
      <main className="pt-20">
        <article className="px-6 md:px-10 max-w-4xl mx-auto py-10 md:py-16">
          <nav className="text-xs text-warm-400 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-tealwarm-600 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-warm-600">Itaobuy Spreadsheet Accessori</span>
          </nav>

          <header>
            <span className="inline-block px-3 py-1 rounded-full bg-tealwarm-100 text-tealwarm-700 text-xs font-semibold uppercase tracking-wide mb-4">
              Categoria Accessori
            </span>
            <h1 className="font-display text-3xl md:text-5xl font-bold text-warm-950 leading-tight">
              <span className="text-tealwarm-600">Itaobuy Spreadsheet Accessori:</span> Occhiali, Orologi e Molto Altro
            </h1>
            <p className="mt-4 text-sm md:text-base text-warm-600 leading-relaxed">
              Gli accessori completano ogni outfit e fanno la differenza nel look quotidiano. Con <strong>itaobuy spreadsheet accessori</strong> puoi esplorare una selezione curata di occhiali da sole, orologi, gioielli e articoli di tendenza — tutti documentati con foto QC che evidenziano materiali, chiusure e finiture nei minimi dettagli.
            </p>
          </header>

          <div className="mt-10">
            <img
              src="https://readdy.ai/api/search-image?query=Carefully%20styled%20flat%20lay%20of%20premium%20accessories%20including%20tortoiseshell%20sunglasses%20a%20minimalist%20gold%20watch%20a%20braided%20leather%20bracelet%20and%20a%20thin%20chain%20necklace%20arranged%20on%20a%20textured%20stone%20surface%20with%20scattered%20dried%20botanicals%2C%20soft%20warm%20lighting%20from%20above%2C%20luxury%20editorial%20still%20life%20photography%20with%20elegant%20composition&width=1200&height=600&seq=article-accessori-hero&orientation=landscape"
              alt="Itaobuy Spreadsheet Accessori occhiali"
              title="Itaobuy Spreadsheet Accessori"
              className="w-full rounded-2xl"
            />
          </div>

          <section className="mt-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">
              Cosa rende speciale la categoria <span className="text-tealwarm-600">accessori</span>
            </h2>
            <p className="mt-4 text-sm md:text-base text-warm-700 leading-relaxed">
              A differenza di altre categorie, gli accessori richiedono un&apos;attenzione particolare ai dettagli: una fibbia, un&apos;incisione, la qualità di una lente. Ecco perché l&apos;<strong>itaobuy spreadsheet</strong> dedica a questa categoria foto QC estremamente ravvicinate, che ti permettono di valutare ogni singolo elemento prima di procedere. Dalle montature degli occhiali ai quadranti degli orologi, nulla viene lasciato al caso.
            </p>
          </section>

          <section className="mt-10">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">
              Tipi di accessori disponibili
            </h2>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { title: 'Occhiali da Sole', desc: 'Montature classiche, sportive e di tendenza. Controlla la qualità delle lenti, la simmetria e la robustezza delle cerniere nelle foto QC.', icon: 'ri-sun-line' },
                { title: 'Orologi', desc: 'Modelli analogici, digitali e smartwatch. Verifica quadrante, cinturino e retro cassa: le foto QC mostrano ogni dettaglio.', icon: 'ri-timer-line' },
                { title: 'Gioielli', desc: 'Collane, bracciali, anelli e orecchini. Controlla chiusure, lucidatura e peso visivo del metallo nelle immagini ravvicinate.', icon: 'ri-vip-diamond-line' },
                { title: 'Portafogli e Piccola Pelletteria', desc: 'Articoli in pelle e materiali sintetici. Verifica cuciture, scomparti interni e qualità della pelle.', icon: 'ri-wallet-3-line' },
              ].map((item) => (
                <div key={item.title} className="bg-cream-50 rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-tealwarm-100 flex items-center justify-center text-tealwarm-700">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <h3 className="font-bold text-warm-900">{item.title}</h3>
                  </div>
                  <p className="text-sm text-warm-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-12">
            <div className="bg-warm-50 rounded-2xl p-6 md:p-8">
              <h3 className="font-display text-xl font-bold text-warm-950 flex items-center gap-2">
                <i className="ri-sparkling-line text-tealwarm-600"></i> Perché scegliere gli accessori via spreadsheet
              </h3>
              <p className="mt-3 text-sm md:text-base text-warm-700 leading-relaxed">
                Acquistare accessori senza vederli dal vivo può sembrare un azzardo. Ma con le foto QC dell&apos;<strong>itaobuy spreadsheet</strong> hai una visione a 360 gradi: puoi zoommare su incisioni, valutare la rifinitura dei bordi e confrontare fino a dieci prodotti simili in pochi minuti. È il modo più intelligente per scegliere accessori di qualità senza sorprese.
              </p>
            </div>
          </section>

          <section className="mt-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">Domande frequenti</h2>
            <div className="mt-5 space-y-4">
              {[
                { q: 'Quali accessori posso trovare su Itaobuy Spreadsheet?', a: 'Occhiali da sole, orologi di tendenza, gioielli come collane e bracciali, portafogli, cinture e piccoli articoli di pelletteria con foto QC.' },
                { q: 'Le foto QC degli accessori mostrano i dettagli più piccoli?', a: 'Sì, le foto QC per gli accessori sono particolarmente dettagliate: mostrano chiusure, incisioni, materiali e finiture che fanno la differenza.' },
                { q: 'Come trovo rapidamente gli accessori su Itaobuy Spreadsheet?', a: 'Apri lo spreadsheet e seleziona la categoria Accessori. Puoi filtrare per tipo di articolo, prezzo e popolarità.' },
              ].map((item, i) => (
                <div key={i} className="bg-cream-50 rounded-2xl p-5">
                  <h3 className="font-bold text-warm-900">{item.q}</h3>
                  <p className="mt-2 text-sm text-warm-600 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          <div className="mt-12 bg-gradient-to-r from-warm-50 to-cream-50 rounded-2xl p-8 text-center">
            <h2 className="font-display text-2xl font-bold text-warm-950">
              Scopri gli <span className="text-tealwarm-600">accessori</span> su Itaobuy Sheet
            </h2>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com/search-920/name/Shoes.html" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-warm-900 text-white text-sm font-semibold hover:bg-warm-800 transition-all whitespace-nowrap cursor-pointer">
                Accessori su Itaobuy Sheet <i className="ri-arrow-right-up-line"></i>
              </a>
              <Link to="/" className="text-sm text-tealwarm-700 hover:text-tealwarm-800 font-medium transition-colors">
                <i className="ri-arrow-left-line mr-1"></i> Torna alla homepage
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}